/* =========================================================================
   Product Drawer — desktop AJAX overlay
   ========================================================================= */
(function () {
  'use strict';

  const DESKTOP_MIN = 990;
  const SECTION_ID = 'main-product-drawer';

  class ProductDrawer {
    constructor() {
      this.dialog = document.getElementById('ProductDrawer');
      if (!this.dialog) return;

      this.content = this.dialog.querySelector('[data-product-drawer-content]');
      this.previousUrl = null;
      this.lastFocusedElement = null;
      this.abortController = null;

      this._onDialogClose = this._onDialogClose.bind(this);
      this._onDialogClick = this._onDialogClick.bind(this);
      this._onContentClick = this._onContentClick.bind(this);
      this._onPopState = this._onPopState.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);

      this.dialog.addEventListener('close', this._onDialogClose);
      this.dialog.addEventListener('click', this._onDialogClick);
      this.content.addEventListener('click', this._onContentClick);
      window.addEventListener('popstate', this._onPopState);
      document.addEventListener('click', this._onDocumentClick);

      // Page-mode details toggle (outside the dialog)
      document.addEventListener('click', (event) => {
        const detailsBtn = event.target.closest('[data-product-drawer-details]');
        if (!detailsBtn) return;
        if (event.target.closest('[data-product-drawer-content]')) return;
        event.preventDefault();
        const root = detailsBtn.closest('product-info') || document;
        const target = root.querySelector('[data-product-drawer-details-target]');
        if (target) {
          target.hidden = !target.hidden;
          detailsBtn.textContent = target.hidden ? 'Altri dettagli ›' : 'Meno dettagli ‹';
        }
      });
    }

    _onDocumentClick(event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (window.innerWidth < DESKTOP_MIN) return;

      const link = event.target.closest('a[data-product-drawer-trigger]');
      if (!link) return;

      /* Il drawer e' una scorciatoia mentre si naviga dentro il sito, quindi
         vale ovunque: home, ricerca, guide, collezioni. Prima era limitato alle
         sole collezioni, ed era un residuo di quando la funzione e' nata per le
         griglie di collezione: da qualunque altra pagina lo stesso identico
         prodotto si apriva a pagina intera, senza una ragione.

         L'unica esclusione e' la scheda prodotto stessa. Non serve a oggi,
         perche' templates/product.json monta la sola sezione
         main-product-drawer e li' non ci sono prodotti da cliccare. Serve a
         domani: aprendo il drawer sopra una scheda prodotto il documento
         conterrebbe due card disponibilita', e alla chiusura il watcher di
         variante resterebbe agganciato all'input del drawer ormai staccato,
         lasciando la card sottostante ferma sull'ultimo dato scritto. Il
         difetto e' gia' noto e documentato come dormiente. Se un giorno si
         aggiungono i prodotti correlati alla scheda, va chiuso prima di
         togliere questa riga. */
      const bodyTpl = document.body.getAttribute('data-template') || '';
      if (/^product/.test(bodyTpl)) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

      event.preventDefault();
      this.open(href);
    }

    /**
     * Open the drawer for a given product URL.
     * Fetches the product section HTML and injects it, then opens the dialog.
     */
    async open(productUrl) {
      if (window.innerWidth < DESKTOP_MIN) {
        window.location.href = productUrl;
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.lastFocusedElement = document.activeElement;
      this.previousUrl = window.location.href;

      this.dialog.setAttribute('data-state', 'loading');
      this.content.innerHTML = '';

      try {
        if (!this.dialog.open) this.dialog.showModal();

        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('section_id', SECTION_ID);

        const response = await fetch(url.toString(), {
          signal: this.abortController.signal,
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('Fetch failed: ' + response.status);

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('product-info.product-drawer');
        if (!section) throw new Error('Section markup not found in response');

        section.classList.remove('product-drawer--page');
        section.classList.add('product-drawer--overlay');

        this._injectStylesheets(parsed);
        this.content.appendChild(section);
        this.dialog.setAttribute('data-state', 'open');
        document.documentElement.classList.add('pd-no-scroll');
        document.body.classList.add('pd-no-scroll');

        // Move the custom zoom cursor into the dialog so it can be rendered
        // inside the top-layer (otherwise it would be painted below the modal).
        const cursorEl = document.querySelector('[data-pd-cursor]');
        if (cursorEl && cursorEl.parentNode !== this.dialog) {
          this.dialog.appendChild(cursorEl);
        }

        history.pushState({ productDrawer: true }, '', productUrl);

        this._executeScripts(section);
        attachVersionsScrollDots(section);

        /* La sezione arriva via AJAX: i custom element gia' registrati sulla
           pagina (cz-store-locator) non vengono ricreati e non hanno modo di
           accorgersi che esiste una nuova scheda prodotto. Senza questo
           evento la card "trova in boutique" del drawer resta com'e' nata nel
           Liquid e non viene mai popolata. */
        document.dispatchEvent(new CustomEvent('cz:product-drawer:loaded', { detail: { section } }));

        const focusTarget = section.querySelector('[data-product-drawer-close]') || section;
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[product-drawer] failed to open:', err);
        this.close();
        window.location.href = productUrl;
      }
    }

    close() {
      if (this.dialog.open) {
        this.dialog.close();
      }
    }

    _onDialogClose() {
      this.dialog.setAttribute('data-state', 'closed');

      // Move the cursor element back out of the dialog before its content
      // gets cleared, otherwise it would be lost.
      const cursorEl = this.dialog.querySelector('[data-pd-cursor]');
      if (cursorEl) document.body.appendChild(cursorEl);

      this.content.innerHTML = '';
      document.documentElement.classList.remove('pd-no-scroll');
      document.body.classList.remove('pd-no-scroll');

      if (this.previousUrl && history.state && history.state.productDrawer) {
        history.back();
      }

      if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
      this.lastFocusedElement = null;
      this.previousUrl = null;
    }

    _onDialogClick(event) {
      const rect = this.dialog.getBoundingClientRect();
      const inDialog =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inDialog) {
        this.close();
      }
    }

    _onContentClick(event) {
      const closeBtn = event.target.closest('[data-product-drawer-close]');
      if (closeBtn) {
        event.preventDefault();
        this.close();
        return;
      }

      const detailsBtn = event.target.closest('[data-product-drawer-details]');
      if (detailsBtn) {
        event.preventDefault();
        const root = detailsBtn.closest('product-info') || this.content;
        const target = root.querySelector('[data-product-drawer-details-target]');
        if (target) {
          target.hidden = !target.hidden;
          detailsBtn.textContent = target.hidden ? 'Altri dettagli ›' : 'Meno dettagli ‹';
        }
      }
    }

    _onPopState(event) {
      if (this.dialog.open) {
        this.dialog.close();
      }
    }

    /**
     * Inject <link rel="stylesheet"> tags from the fetched section response
     * into <head>, deduplicating by href. Without this, CSS that is normally
     * included inline by the section would be missing for AJAX-injected content.
     */
    _injectStylesheets(parsed) {
      const links = parsed.querySelectorAll('link[rel="stylesheet"]');
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (!href) return;
        const existing = document.querySelector('link[rel="stylesheet"][href="' + href + '"]');
        if (existing) return;
        const clone = document.createElement('link');
        clone.rel = 'stylesheet';
        clone.href = href;
        const media = link.getAttribute('media');
        if (media) clone.media = media;
        document.head.appendChild(clone);
      });
    }

    /**
     * Re-execute inline <script> tags from injected HTML.
     * External script tags are skipped (they're already loaded in the page).
     */
    _executeScripts(container) {
      const scripts = container.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        for (const attr of oldScript.attributes) {
          newScript.setAttribute(attr.name, attr.value);
        }
        if (!oldScript.src) {
          newScript.textContent = oldScript.textContent;
        }
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }
  }

  /* =========================================================================
     WhisperPill — sticky pill ATC visible quando il gruppo CTA (Aggiungi +
     Contatta un consulente) è stato scrollato OLTRE (interamente sopra
     il viewport). Page mode only — overlay drawer ha già il CTA visibile.
     ========================================================================= */
  class WhisperPill {
    constructor() {
      this.pill = document.querySelector('[data-product-drawer-pill]');
      if (!this.pill) return;
      const root = document.querySelector('.product-drawer');
      const renderMode = root && root.dataset.renderMode;
      if (renderMode !== 'page') return;

      this.ctaGroup = document.querySelector('.product-drawer__cta');
      if (!this.ctaGroup) return;

      this.pill.hidden = false;

      this._observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          /* boundingClientRect.bottom < 0 → container interamente sopra
             il viewport (utente ha già passato sia Aggiungi sia Contatta).
             Se il container è in viewport o ancora sotto, pill nascosta. */
          const scrolledPast = entry.boundingClientRect.bottom < 0;
          this.pill.setAttribute('data-visible', scrolledPast ? 'true' : 'false');
        }
      }, { threshold: 0 });

      this._observer.observe(this.ctaGroup);
    }
  }

  function attachVersionsScrollDots(root) {
    const scope = root || document;
    const dotsContainers = scope.querySelectorAll('.product-drawer__versions-dots');
    dotsContainers.forEach((container) => {
      const strip = container.previousElementSibling;
      if (!strip || !strip.classList.contains('product-drawer__versions')) return;
      const dots = container.querySelectorAll('span');
      if (!dots.length) return;
      const first = strip.querySelector('.product-drawer__version');
      if (!first) return;

      const update = () => {
        const gap = parseFloat(getComputedStyle(strip).gap) || 0;
        const step = first.getBoundingClientRect().width + gap;
        if (step <= 0) return;

        // visibleFull = quanti item interi entrano nel viewport (+gap "fantasma" oltre l'ultimo)
        const visibleFull = Math.max(1, Math.floor((strip.clientWidth + gap) / step));
        const requiredDots = Math.max(1, dots.length - visibleFull + 1);

        dots.forEach((d, i) => d.classList.toggle('is-hidden', i >= requiredDots));
        container.classList.toggle('is-empty', requiredDots <= 1);

        // mappa lo scrollLeft come ratio 0..1 sul range dei dots utili
        // così l'ultimo dot è SEMPRE attivo a fine scroll, anche se l'ultimo item
        // non è snap-allineato a sinistra (scroll-snap-type bloccato da maxScroll)
        const max = strip.scrollWidth - strip.clientWidth;
        const ratio = max > 0 ? Math.max(0, Math.min(1, strip.scrollLeft / max)) : 0;
        const idx = Math.round(ratio * (requiredDots - 1));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      };

      strip.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  function init() {
    window.productDrawer = new ProductDrawer();
    window.productDrawerPill = new WhisperPill();
    attachVersionsScrollDots(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
